import { useFormikContext } from "formik";
import { AddressInput } from "@volca/ui/components/ui/address-input";
import { DateTimePicker } from "@volca/ui/components/ui/date-time-picker";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Label } from "@volca/ui/components/ui/label";
import { MoneyInput } from "@volca/ui/components/ui/money-input";
import { Stack } from "@volca/ui/components/ui/stack";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { dayjs } from "@/core/utils/date";
import { REFERRAL_FIELD, REFERRAL_FIELD_TO_LABEL } from "@/referrals/constants";
import type {
  ColumnToUpdateModalInfo,
  IReferral,
  ReferralUpdateFormValues,
} from "@/referrals/referral";

export interface BaseContentProps {
  referral: IReferral;
  modalInfo: ColumnToUpdateModalInfo;
}

export const BaseContent = ({ referral, modalInfo }: BaseContentProps) => {
  const { values, touched, errors, getFieldProps, setFieldValue } =
    useFormikContext<ReferralUpdateFormValues>();

  const {
    ifNeededFormFields: ifNeededFields = [],
    constantFormFields: constantFields = [],
    requiredFields = [],
  } = modalInfo;

  // console.log("errors", errors);

  const renderField = (field: keyof typeof REFERRAL_FIELD) => {
    const label =
      REFERRAL_FIELD_TO_LABEL[field as keyof typeof REFERRAL_FIELD_TO_LABEL];

    switch (field) {
      case REFERRAL_FIELD.address:
        return (
          <AddressInput
            key={field}
            value={
              values.address || {
                line_one: "",
                line_two: "",
                city: "",
                state: "",
                zip_code: "",
                country: "",
              }
            }
            onChange={(value) => setFieldValue("address", value)}
            required={requiredFields.includes(field)}
          />
        );
      case REFERRAL_FIELD.deal_size:
        return (
          <MoneyInput
            key={field}
            name="deal_size"
            label={label}
            value={values.deal_size || ""}
            onChange={(value) => setFieldValue("deal_size", value)}
            error={
              touched.deal_size && values.deal_size
                ? (errors.deal_size as string)
                : undefined
            }
            required={requiredFields.includes(field)}
            fullWidth
          />
        );
      case REFERRAL_FIELD.additional_info:
        return (
          <Stack key={field} gap={2} className="w-full">
            <Label htmlFor={field}>{label}</Label>
            <Textarea
              id={field}
              className="min-h-32"
              placeholder={label}
              {...getFieldProps("additional_info")}
            />
          </Stack>
        );
      case REFERRAL_FIELD.appointment_date:
        return (
          <DateTimePicker
            key={field}
            name={field}
            label={label}
            placeholder="mm/dd/yyyy"
            fullWidth
            value={
              values.appointment_date
                ? dayjs(values.appointment_date).toDate()
                : null
            }
            onChange={(date) => {
              setFieldValue(
                "appointment_date",
                date ? date.toISOString() : null,
              );
            }}
            error={
              touched.appointment_date && errors.appointment_date
                ? (errors.appointment_date as string)
                : undefined
            }
          />
        );
      default:
        return (
          <FormInput
            key={field}
            label={label}
            error={
              touched[field as keyof ReferralUpdateFormValues] &&
              values[field as keyof ReferralUpdateFormValues]
                ? (errors[field as keyof ReferralUpdateFormValues] as string)
                : undefined
            }
            required={requiredFields.includes(field)}
            {...getFieldProps(field)}
            fullWidth
          />
        );
    }
  };

  const neededFields = ifNeededFields.filter((field) => !referral[field]);

  if (neededFields.length > 0) {
    return (
      <Stack className="w-full" align="start" gap={6}>
        {neededFields.map(renderField)}
        {constantFields.map(renderField)}
      </Stack>
    );
  }

  return (
    <Stack className="w-full" align="start" gap={6}>
      {constantFields.map(renderField)}
    </Stack>
  );
};
